import Keycloak from "keycloak-js";
import {
    KEYCLOAK_URL,
    KEYCLOAK_REALM,
    KEYCLOAK_CLIENT_ID,
    KEYCLOAK_ROLE_MASTER,
    KEYCLOAK_ROLE_FOLHA,
    KEYCLOAK_ROLE_FINANCEIRA,
    AMBIENTE,
    HABILITA_MASSA_MOCKADA,
    CONECTOR_ESFINGE_URL
} from "./env";
import {
    appRoot,
    currentDestination,
    rememberDestination,
    rememberedDestination,
    restoreDestination,
    safeDestination
} from "./redirects";

export const authRequired = Boolean(
    KEYCLOAK_URL && KEYCLOAK_REALM && KEYCLOAK_CLIENT_ID
);
const configured = [KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT_ID].filter(
    Boolean
).length;
const loopback = (host) => ["localhost", "127.0.0.1", "[::1]"].includes(host);
// O modo sem login existe somente para a simulação local explícita já oferecida pelo projeto.
export const localMock =
    configured === 0 &&
    AMBIENTE === "MOCK" &&
    HABILITA_MASSA_MOCKADA &&
    loopback(window.location.hostname) &&
    loopback(new URL(CONECTOR_ESFINGE_URL, window.location.origin).hostname);
const configurationValid =
    authRequired &&
    [KEYCLOAK_ROLE_MASTER, KEYCLOAK_ROLE_FOLHA, KEYCLOAK_ROLE_FINANCEIRA].every(
        (role) => typeof role === "string" && role.trim()
    );
const keycloak = new Keycloak({
    url: KEYCLOAK_URL,
    realm: KEYCLOAK_REALM,
    clientId: KEYCLOAK_CLIENT_ID
});
export const ROLES_ESFINGE = Object.freeze({
    MASTER: KEYCLOAK_ROLE_MASTER,
    FOLHA: KEYCLOAK_ROLE_FOLHA,
    FINANCEIRA: KEYCLOAK_ROLE_FINANCEIRA
});
const listeners = new Set();
const invalidators = new Set();
let phase = localMock
    ? "ready"
    : configurationValid
    ? "initializing"
    : "configuration";
let deniedByService = false;
let refresh;
let forcedRefresh;
let initialization;
let revision = 0;
let previousScope = "";
let lastUser = null;
const LOGGED_OUT = "esfinge.logged-out";

export const getToken = () => keycloak.token;
export function getRealmRoles(tokenParsed = keycloak.tokenParsed) {
    const roles = tokenParsed?.realm_access?.roles;
    return Array.isArray(roles)
        ? roles.filter((role) => typeof role === "string")
        : [];
}
export function hasAnyRealmRole(tokenParsed, roles) {
    return (
        Array.isArray(roles) &&
        roles.some(
            (role) => Boolean(role) && getRealmRoles(tokenParsed).includes(role)
        )
    );
}
export function getUser(token = keycloak.tokenParsed) {
    const text = (value) => (typeof value === "string" ? value.trim() : "");
    const username = text(token?.preferred_username);
    return {
        name:
            text(token?.name) ||
            [text(token?.given_name), text(token?.family_name)]
                .filter(Boolean)
                .join(" ") ||
            username ||
            "Usuário autenticado",
        username
    };
}
export function getSessionStatus() {
    const authenticated = Boolean(keycloak.authenticated) && phase === "ready";
    const hasRoles = hasAnyRealmRole(
        keycloak.tokenParsed,
        Object.values(ROLES_ESFINGE)
    );
    const status =
        phase !== "ready"
            ? phase
            : localMock
            ? "authorized"
            : !authenticated
            ? "expired"
            : deniedByService || !hasRoles
            ? "denied"
            : "authorized";
    return {
        status,
        authenticated,
        authRequired,
        localMock,
        hasAccess: status === "authorized",
        user: keycloak.authenticated ? getUser() : lastUser,
        revision
    };
}
function notify() {
    if (keycloak.authenticated) lastUser = getUser();
    const session = getSessionStatus();
    const scope = JSON.stringify([
        session.status,
        keycloak.tokenParsed?.sub,
        getRealmRoles().slice().sort()
    ]);
    if (scope !== previousScope) {
        revision++;
        previousScope = scope;
    }
    invalidators.forEach((listener) => listener());
    listeners.forEach((listener) => listener());
}
export function subscribeSession(listener, {priority = false} = {}) {
    const subscribers = priority ? invalidators : listeners;
    subscribers.add(listener);
    return () => subscribers.delete(listener);
}
export function hasAnyRole(...roles) {
    return (
        getSessionStatus().hasAccess &&
        (localMock || hasAnyRealmRole(keycloak.tokenParsed, roles))
    );
}
export const canAccessAnyModule = () =>
    hasAnyRole(...Object.values(ROLES_ESFINGE));
export const canAccessMaster = () => hasAnyRole(ROLES_ESFINGE.MASTER);
export const canAccessFolha = () =>
    hasAnyRole(ROLES_ESFINGE.MASTER, ROLES_ESFINGE.FOLHA);
export const canAccessFinanceira = () =>
    hasAnyRole(ROLES_ESFINGE.MASTER, ROLES_ESFINGE.FINANCEIRA);
export const canAuthorizeFinanceira = () =>
    authRequired && canAccessFinanceira();
export const canAuthorizeFolha = () => authRequired && canAccessFolha();
export const canReconcileFornecedor = () => authRequired && canAccessMaster();
export function negarAcesso() {
    deniedByService = true;
    notify();
}
export function confirmarAcesso() {
    deniedByService = false;
    notify();
}
export function invalidarSessao() {
    if (["expired", "signed-out"].includes(phase)) return;
    phase = "expired";
    notify();
}
export function sessionError() {
    const {status} = getSessionStatus();
    const denied = status === "denied";
    const configuration = status === "configuration";
    return Object.assign(
        new Error(
            configuration
                ? "A configuração de autenticação está ausente ou incompleta. Contate o suporte."
                : denied
                ? "Seu usuário não possui permissão para acessar o conector e-Sfinge."
                : "Sua sessão expirou. Entre novamente para continuar."
        ),
        {
            status: configuration ? 503 : denied ? 403 : 401,
            code: configuration
                ? "AUTENTICACAO_NAO_CONFIGURADA"
                : denied
                ? "ACESSO_NEGADO"
                : "SESSAO_EXPIRADA",
            isSessionError: true
        }
    );
}
export function assertAccess({probe = false} = {}) {
    const session = getSessionStatus();
    if (
        !session.hasAccess &&
        !(
            probe &&
            session.authenticated &&
            hasAnyRealmRole(keycloak.tokenParsed, Object.values(ROLES_ESFINGE))
        )
    )
        throw sessionError();
}
export function updateToken(minValidity = 30) {
    if (localMock) return Promise.resolve(false);
    if (!configurationValid || phase !== "ready")
        return Promise.reject(sessionError());
    if (refresh) {
        // Uma verificação explícita não pode ser satisfeita por um updateToken que não renovou nada.
        if (minValidity === -1 && !forcedRefresh) {
            forcedRefresh = refresh
                .then((renewed) => (renewed ? renewed : updateToken(-1)))
                .finally(() => {
                    forcedRefresh = undefined;
                });
        }
        return minValidity === -1 ? forcedRefresh : refresh;
    }
    refresh = Promise.resolve()
        .then(() => keycloak.updateToken(minValidity))
        .then((renewed) => {
            if (
                phase !== "ready" ||
                !keycloak.token ||
                keycloak.isTokenExpired(0)
            )
                throw sessionError();
            notify();
            return renewed;
        })
        .catch(() => {
            invalidarSessao();
            throw sessionError();
        })
        .finally(() => {
            refresh = undefined;
        });
    if (minValidity === -1)
        forcedRefresh = refresh.finally(() => {
            forcedRefresh = undefined;
        });
    return minValidity === -1 ? forcedRefresh : refresh;
}
function storage(action, value) {
    try {
        return action === "get"
            ? sessionStorage.getItem(LOGGED_OUT)
            : action === "set"
            ? sessionStorage.setItem(LOGGED_OUT, value)
            : sessionStorage.removeItem(LOGGED_OUT);
    } catch {
        return null;
    }
}
export function login(options = {}) {
    if (!configurationValid) return Promise.reject(sessionError());
    const destination = safeDestination(
        options.returnTo ||
            (phase === "signed-out"
                ? rememberedDestination()
                : currentDestination())
    );
    rememberDestination(destination);
    storage("remove");
    return keycloak.login({redirectUri: appRoot()});
}
export function createLogoutUrl() {
    const url = new URL(keycloak.createLogoutUrl({redirectUri: appRoot()}));
    url.searchParams.delete("redirect_uri");
    url.searchParams.set("post_logout_redirect_uri", appRoot());
    url.searchParams.set("client_id", KEYCLOAK_CLIENT_ID);
    if (keycloak.idToken)
        url.searchParams.set("id_token_hint", keycloak.idToken);
    return url.toString();
}
export function logout() {
    const url = createLogoutUrl();
    rememberDestination(currentDestination());
    storage("set", "true");
    phase = "signed-out";
    notify();
    keycloak.clearToken();
    window.location.assign(url);
}
export function initKeycloak() {
    if (initialization) return initialization;
    if (!configurationValid) {
        notify();
        return Promise.resolve();
    }
    keycloak.onTokenExpired = () => {
        updateToken(30).catch(() => {});
    };
    keycloak.onAuthLogout = invalidarSessao;
    // Sem login-required: clearToken/refresh recusado não inicia outro login automaticamente.
    initialization = keycloak
        .init({pkceMethod: "S256", redirectUri: appRoot()})
        .then((authenticated) => {
            if (authenticated) {
                phase = "ready";
                restoreDestination();
                notify();
            } else if (storage("get")) {
                phase = "signed-out";
                notify();
            } else {
                return login();
            }
        })
        .catch(() => {
            phase = "error";
            notify();
        });
    return initialization;
}
